import { useMemo, useReducer } from "react";
import type { Dispatch, SetStateAction } from "react";
import type {
  CommunityContributionData,
  DiversityInclusionData,
  EmployeeSupportData,
  HumanRightsData,
  SocialData,
  TransitionMeasure,
} from "./types";

type SocialState = {
  employeeSupportData: EmployeeSupportData;
  humanRightsData: HumanRightsData;
  diversityInclusionData: DiversityInclusionData;
  communityContributionData: CommunityContributionData;
  socialData: SocialData;
  transitionMeasuresByTopic: Record<string, TransitionMeasure[]>;
};

type SocialAction =
  | { type: "setEmployeeSupportData"; payload: SetStateAction<EmployeeSupportData> }
  | { type: "updateEmployeeSupportData"; payload: Partial<EmployeeSupportData> }
  | { type: "setHumanRightsData"; payload: SetStateAction<HumanRightsData> }
  | { type: "updateHumanRightsData"; payload: Partial<HumanRightsData> }
  | { type: "setDiversityInclusionData"; payload: SetStateAction<DiversityInclusionData> }
  | { type: "updateDiversityInclusionData"; payload: Partial<DiversityInclusionData> }
  | { type: "setCommunityContributionData"; payload: SetStateAction<CommunityContributionData> }
  | { type: "updateCommunityContributionData"; payload: Partial<CommunityContributionData> }
  | { type: "setSocialData"; payload: SetStateAction<SocialData> }
  | { type: "updateSocialData"; payload: Partial<SocialData> }
  | { type: "setTransitionMeasuresForTopic"; payload: { topicId: string; measures: TransitionMeasure[] } };

const initialSocialState: SocialState = {
  employeeSupportData: {
    permanentEmployees: "",
    temporaryEmployees: "",
    femaleEmployees: "",
    maleEmployees: "",
    employeesByCountry: "",
    turnoverRate: "",
    recordableAccidents: "",
    accidentRate: "",
    workRelatedFatalities: "",
    minimumWageCompliance: null,
    collectiveBargainingPercent: "",
    trainingHoursFemale: "",
    trainingHoursMale: "",
    genderPayGapPercent: "",
  },
  humanRightsData: {
    hasHumanRightsPolicy: null,
    policyCovers: [],
    hasGrievanceMechanism: null,
    workforceIncidents: null,
    workforceIncidentsDescription: "",
    valueChainIncidents: null,
    valueChainIncidentsDescription: "",
    evidenceFiles: [],
  },
  diversityInclusionData: {
    femaleManagers: "",
    maleManagers: "",
    selfEmployedWorkers: "",
    temporaryAgencyWorkers: "",
    diversityPolicies: "",
    evidenceFiles: [],
  },
  communityContributionData: {
    communityPrograms: "",
    charitableGivingAmount: "",
    charitableGivingCurrency: "usd",
    volunteerHours: "",
    measurableOutcomes: "",
    evidenceFiles: [],
  },
  socialData: {
    permanentEmployees: "",
    temporaryEmployees: "",
    femaleEmployees: "",
    maleEmployees: "",
    recordableAccidents: "",
    workRelatedFatalities: "",
    safetyTrainingHours: "",
    minimumWageCompliance: null,
    genderPayGapPercent: "",
    collectiveBargainingPercent: "",
    avgTrainingHoursPerEmployee: "",
    femaleManagementPercent: "",
    femaleBoardPercent: "",
    childLaborPolicy: null,
    forcedLaborPolicy: null,
    supplierCodeOfConduct: null,
    humanRightsNotes: "",
    diversityVoiceNote: "",
  },
  transitionMeasuresByTopic: {},
};

function resolveSetState<T>(value: SetStateAction<T>, current: T): T {
  return typeof value === "function" ? value(current) : value;
}

function socialReducer(state: SocialState, action: SocialAction): SocialState {
  switch (action.type) {
    case "setEmployeeSupportData":
      return { ...state, employeeSupportData: resolveSetState(action.payload, state.employeeSupportData) };
    case "updateEmployeeSupportData":
      return { ...state, employeeSupportData: { ...state.employeeSupportData, ...action.payload } };
    case "setHumanRightsData":
      return { ...state, humanRightsData: resolveSetState(action.payload, state.humanRightsData) };
    case "updateHumanRightsData":
      return { ...state, humanRightsData: { ...state.humanRightsData, ...action.payload } };
    case "setDiversityInclusionData":
      return { ...state, diversityInclusionData: resolveSetState(action.payload, state.diversityInclusionData) };
    case "updateDiversityInclusionData":
      return { ...state, diversityInclusionData: { ...state.diversityInclusionData, ...action.payload } };
    case "setCommunityContributionData":
      return { ...state, communityContributionData: resolveSetState(action.payload, state.communityContributionData) };
    case "updateCommunityContributionData":
      return { ...state, communityContributionData: { ...state.communityContributionData, ...action.payload } };
    case "setSocialData":
      return { ...state, socialData: resolveSetState(action.payload, state.socialData) };
    case "updateSocialData":
      return { ...state, socialData: { ...state.socialData, ...action.payload } };
    case "setTransitionMeasuresForTopic":
      return {
        ...state,
        transitionMeasuresByTopic: { ...state.transitionMeasuresByTopic, [action.payload.topicId]: action.payload.measures },
      };
    default:
      return state;
  }
}

type SocialActions = {
  setEmployeeSupportData: Dispatch<SetStateAction<EmployeeSupportData>>;
  updateEmployeeSupportData: (updates: Partial<EmployeeSupportData>) => void;
  setHumanRightsData: Dispatch<SetStateAction<HumanRightsData>>;
  updateHumanRightsData: (updates: Partial<HumanRightsData>) => void;
  setDiversityInclusionData: Dispatch<SetStateAction<DiversityInclusionData>>;
  updateDiversityInclusionData: (updates: Partial<DiversityInclusionData>) => void;
  setCommunityContributionData: Dispatch<SetStateAction<CommunityContributionData>>;
  updateCommunityContributionData: (updates: Partial<CommunityContributionData>) => void;
  setSocialData: Dispatch<SetStateAction<SocialData>>;
  updateSocialData: (updates: Partial<SocialData>) => void;
  setTransitionMeasuresForTopic: (topicId: string, measures: TransitionMeasure[]) => void;
};

export function useSocialState(): { state: SocialState; actions: SocialActions } {
  const [state, dispatch] = useReducer(socialReducer, initialSocialState);

  const actions = useMemo<SocialActions>(
    () => ({
      setEmployeeSupportData: (value) => dispatch({ type: "setEmployeeSupportData", payload: value }),
      updateEmployeeSupportData: (updates) => dispatch({ type: "updateEmployeeSupportData", payload: updates }),
      setHumanRightsData: (value) => dispatch({ type: "setHumanRightsData", payload: value }),
      updateHumanRightsData: (updates) => dispatch({ type: "updateHumanRightsData", payload: updates }),
      setDiversityInclusionData: (value) => dispatch({ type: "setDiversityInclusionData", payload: value }),
      updateDiversityInclusionData: (updates) => dispatch({ type: "updateDiversityInclusionData", payload: updates }),
      setCommunityContributionData: (value) => dispatch({ type: "setCommunityContributionData", payload: value }),
      updateCommunityContributionData: (updates) => dispatch({ type: "updateCommunityContributionData", payload: updates }),
      setSocialData: (value) => dispatch({ type: "setSocialData", payload: value }),
      updateSocialData: (updates) => dispatch({ type: "updateSocialData", payload: updates }),
      setTransitionMeasuresForTopic: (topicId, measures) =>
        dispatch({ type: "setTransitionMeasuresForTopic", payload: { topicId, measures } }),
    }),
    []
  );

  return { state, actions };
}
